/**
 * Extracts the first URL from post content and fetches link metadata via /posts/link-preview.
 * Suppressed when the post already has an image. YouTube/Vimeo URLs render a VideoCard;
 * all other URLs render a LinkCard.
 */
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, PlayCircle } from 'lucide-react'
import { postsApi } from '@/api'
import type { LinkPreviewResult } from '@/api/endpoints/posts'

interface LinkPreviewProps {
  content: string
  hasImage: boolean
}

function extractUrl(text: string): string | null {
  const match = text.match(/https?:\/\/[^\s<>"]+/i)
  return match?.[0] ?? null
}

function getHostname(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

function VideoCard({ preview }: { preview: LinkPreviewResult }) {
  const thumbnailUrl =
    preview.type === 'youtube' && preview.videoId
      ? `https://img.youtube.com/vi/${preview.videoId}/hqdefault.jpg`
      : preview.image

  if (!thumbnailUrl) return null

  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        margin: '8px 16px 0',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        position: 'relative',
        textDecoration: 'none',
      }}
    >
      <img
        src={thumbnailUrl}
        alt={preview.title ?? ''}
        style={{
          width: '100%',
          aspectRatio: '16/9',
          objectFit: 'cover',
          display: 'block',
        }}
      />
      {/* Play button overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PlayCircle size={28} color="#fff" />
        </div>
      </div>
      {/* Domain label */}
      <div
        style={{
          padding: '6px 10px',
          fontSize: 'var(--fs-xs)',
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-muted)',
          background: 'var(--surface)',
        }}
      >
        {getHostname(preview.url)}
      </div>
    </a>
  )
}

function LinkCard({ preview }: { preview: LinkPreviewResult }) {
  return (
    <div
      role="link"
      tabIndex={0}
      onClick={() => window.open(preview.url, '_blank', 'noopener')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') window.open(preview.url, '_blank', 'noopener')
      }}
      style={{
        display: 'flex',
        gap: 12,
        padding: '10px 12px',
        margin: '8px 16px 0',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--surface)',
        cursor: 'pointer',
        textDecoration: 'none',
      }}
    >
      {preview.image && (
        <img
          src={preview.image}
          alt=""
          style={{
            width: 72,
            height: 72,
            objectFit: 'cover',
            borderRadius: 'var(--radius-sm)',
            flexShrink: 0,
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Domain */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 'var(--fs-xs)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-faint)',
            marginBottom: 4,
          }}
        >
          <ExternalLink size={11} />
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getHostname(preview.url)}
          </span>
        </div>

        {/* Title */}
        {preview.title && (
          <div
            style={{
              fontSize: 'var(--fs-sm)',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              color: 'var(--text-strong)',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              marginBottom: 2,
            }}
          >
            {preview.title}
          </div>
        )}

        {/* Description */}
        {preview.description && (
          <div
            style={{
              fontSize: 'var(--fs-xs)',
              fontFamily: 'var(--font-sans)',
              color: 'var(--text-muted)',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
            }}
          >
            {preview.description}
          </div>
        )}
      </div>
    </div>
  )
}

export function LinkPreview({ content, hasImage }: LinkPreviewProps) {
  const url = extractUrl(content)

  const { data } = useQuery({
    queryKey: ['link-preview', url],
    queryFn: () => postsApi.getLinkPreview(url!),
    enabled: !!url && !hasImage,
    staleTime: 1000 * 60 * 10,
  })

  if (!url || hasImage || !data) return null

  if (data.type === 'youtube' || data.type === 'vimeo') {
    return <VideoCard preview={data} />
  }

  return <LinkCard preview={data} />
}
